import type { JourneyStage, Engagement, Theme } from './types';
import { asText } from './asText';
import { normalizeSentiment } from './sentiment';

function asList(v: unknown): string {
  return asText(v);
}

export async function exportJourneyPptx(
  stages: JourneyStage[],
  engagement: Engagement,
  themes: Theme[],
): Promise<void> {
  // Pin stays on pptxgenjs@3.12.0 (see package.json). 4.x uses import('node:fs'),
  // which CRA/webpack 5 rejects as an UnhandledSchemeError during production builds.
  const mod = await import('pptxgenjs');
  const PptxGenJS = (mod as { default?: new () => any }).default || (mod as unknown as new () => any);
  const pptx = new PptxGenJS();
  pptx.title = `${engagement.client || 'Engagement'} — User Journey`;
  pptx.author = 'TSG Synthesis Visualizations';
  const pxc = (hex: string) => (hex || '#333333').replace('#', '');

  const s1 = pptx.addSlide();
  s1.addText(engagement.client || 'Engagement', { x: 0.5, y: 0.3, w: 9, fontSize: 24, bold: true, color: '0F172A' });
  s1.addText('End-to-End User Journey', { x: 0.5, y: 0.7, w: 9, fontSize: 16, color: '475569' });
  s1.addText(`${stages.length} stages · ${engagement.interviews || 0} interviews · ${engagement.window || ''}`, {
    x: 0.5, y: 1.05, w: 9, fontSize: 10, color: '94A3B8',
  });

  const stageW = Math.min(1.4, 8.5 / Math.max(stages.length, 1));
  stages.forEach((st, i) => {
    const x = 0.5 + i * (stageW + 0.08);
    s1.addShape(pptx.ShapeType.roundRect, {
      x, y: 1.6, w: stageW, h: 0.6,
      fill: { color: pxc(st.color || '#7C3AED') }, rectRadius: 0.08,
    });
    s1.addText(st.name || `Stage ${i + 1}`, {
      x, y: 1.6, w: stageW, h: 0.6,
      fontSize: 8, bold: true, color: 'FFFFFF', align: 'center', valign: 'middle',
    });
    s1.addText(`${Math.round(normalizeSentiment(st.sentiment) * 100)}%`, {
      x, y: 2.25, w: stageW, h: 0.25, fontSize: 8, color: '64748B', align: 'center',
    });
  });

  stages.forEach((st, i) => {
    const slide = pptx.addSlide();
    const c = st.color || '#7C3AED';
    slide.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: 10, h: 0.8, fill: { color: pxc(c) } });
    slide.addText(st.name || `Stage ${i + 1}`, { x: 0.5, y: 0.1, w: 7, h: 0.3, fontSize: 18, bold: true, color: 'FFFFFF' });
    slide.addText(`Sentiment: ${Math.round(normalizeSentiment(st.sentiment) * 100)}%`, {
      x: 0.5, y: 0.42, w: 4, h: 0.25, fontSize: 10, color: 'FFFFFF',
    });
    let row = 1.1;
    const attrs: Array<[string, string]> = [
      ['Responsible Team', asList(st.team)],
      ['Duration', asList(st.duration)],
      ['Goals', asList(st.goals)],
      ['KPIs', asList(st.kpi)],
      ['Key Actions', asList(st.actions)],
      ['Experience', asList(st.exp)],
      ['Touchpoints', asList(st.touch)],
    ].filter(([, v]) => v);
    attrs.forEach(([label, value]) => {
      slide.addText(label, { x: 0.5, y: row, w: 1.8, fontSize: 9, bold: true, color: '334155' });
      slide.addText(String(value), { x: 2.4, y: row, w: 7, fontSize: 9, color: '475569' });
      row += 0.32;
    });
    const stageThemes = (st.themes || []).map((rank) => themes.find((t) => t.rank === rank)).filter(Boolean) as Theme[];
    if (stageThemes.length) {
      row += 0.15;
      slide.addText('THEMES', { x: 0.5, y: row, w: 2, fontSize: 8, bold: true, color: '94A3B8' });
      row += 0.25;
      stageThemes.forEach((t) => {
        slide.addText(`#${t.rank} ${t.name}`, { x: 0.7, y: row, w: 8, fontSize: 9, color: '334155' });
        row += 0.28;
      });
    }
    (st.pains || []).forEach((p, idx) => {
      if (idx === 0) {
        row += 0.15;
        slide.addText('PAIN POINTS', { x: 0.5, y: row, w: 2, fontSize: 8, bold: true, color: '94A3B8' });
        row += 0.25;
      }
      slide.addText(`• ${asText(p)}`, { x: 0.7, y: row, w: 8.5, fontSize: 9, color: '475569' });
      row += 0.25;
    });
  });

  const filename = `${(engagement.client || 'engagement').replace(/[^a-zA-Z0-9]/g, '_')}_journey.pptx`;
  await pptx.writeFile({ fileName: filename });
}
